import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Router, NavigationStart } from '@angular/router';
import { QueryParams } from './query-params';
import { EMPTY, Observable, Subject } from 'rxjs';
import { switchMap, filter } from 'rxjs/operators';
import * as _ from 'lodash';
import { ActiveFilterService } from './../query/active-filter.service';
import { ConfigService } from './config.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { Body, ProductQuery, SearchQuery } from '../../models/body';
import InterfaceHttpService from '@/app/core/http/http-service.interface';
import { SimpleRdoHttpService } from './simple-rdo-http.service'; // Assumed simple service import

@Injectable()
export class RdoHttpService implements InterfaceHttpService {
  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private filterService: ActiveFilterService,
    private config: ConfigService,
    private router: Router,
    private simpleRdoHttpService: SimpleRdoHttpService
  ) {
    // Router cancellation handled by simpleRdoHttpService
  }

  /**
   * Delegates abort logic to the composed service for all HttpClient requests.
   */
  public disableRoutingAbort(): InterfaceHttpService {
    this.simpleRdoHttpService.disableRoutingAbort();
    return this;
  }

  public setCancelSubject(sub$: Subject<any>): InterfaceHttpService {
    this.simpleRdoHttpService.setCancelSubject(sub$);
    return this;
  }

  /**
   * Creates a new instance of RdoHttpService with the same dependencies.
   * Each clone has its own shouldAbort flag, allowing isolated control.
   *
   * @returns A new RdoHttpService instance
   *
   * @example
   * this.rdoHttpService.clone().disableRoutingAbort().get('/endpoint')
   */
  public clone(): RdoHttpService {
    return new RdoHttpService(
      this.http,
      this.authenticationService,
      this.filterService,
      this.config,
      this.router,
      this.simpleRdoHttpService.clone() // Just clone the simple service
    );
  }

  // -------- Delegated methods (HttpClient) --------
  public delete = (...args: any[]): Observable<Record<any, any>> =>
    this.simpleRdoHttpService.delete(...args);

  public get = (...args: any[]): Observable<Record<any, any>> =>
    this.simpleRdoHttpService.get(...args);

  public patch = (...args: any[]): Observable<Record<any, any>> =>
    this.simpleRdoHttpService.patch(...args);

  public put = (...args: any[]): Observable<Record<any, any>> =>
    this.simpleRdoHttpService.put(...args);

  public toHttpParams = (params: QueryParams): HttpParams =>
    this.simpleRdoHttpService.toHttpParams(params);

  // -------- Manual XHR download with cancellation --------
  public getFile = (endpoint: string): Observable<Record<any, any>[]> => {
    const request$ = Observable.create((observer) => {
      let xhr: XMLHttpRequest;
      let abortListener: any;
      let isAborted = false; // Track if we've requested an abort

      const sub = this.authenticationService
        .getAuthToken()
        .subscribe((token) => {
          endpoint = endpoint.indexOf('/') === 0 ? endpoint : '/' + endpoint;
          if (
            this.authenticationService &&
            this.authenticationService._userInfoView &&
            this.authenticationService._userInfoView.SelectedClient
          ) {
            let delimit = '?';
            if (endpoint.toLowerCase().indexOf('clientid=') === -1) {
              if (endpoint.indexOf('?') > -1) {
                delimit = '&';
              }
              endpoint =
                endpoint +
                delimit +
                'ClientId=' +
                this.authenticationService._userInfoView.SelectedClient.ClientID.toString();
            }
          }
          this.config.baseApiUrl.subscribe((apiUrl) => {
            const url = apiUrl + endpoint;
            xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.setRequestHeader(
              'Authorization',
              'Bearer ' + token.AccessToken
            );
            xhr.withCredentials = true;
            xhr.responseType = 'blob';

            // Abort XHR on navigation using cancellation service (if enabled)
            const routeCancelSubject =
              this.simpleRdoHttpService.getRouteCancellationSubject();
            if (routeCancelSubject) {
              abortListener = routeCancelSubject.subscribe(() => {
                isAborted = true; // Mark as aborted
                if (xhr && xhr.readyState < 4) {
                  xhr.abort();
                }
              });
            }

            xhr.onreadystatechange = () => {
              if (xhr.readyState === 4) {
                // Don't process response if we've aborted
                if (isAborted) {
                  return;
                }

                if (xhr.status === 200) {
                  const contentType =
                    xhr.getResponseHeader('content-type') ||
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                  const blob = new Blob([xhr.response], { type: contentType });
                  observer.next(blob);
                  observer.complete();
                } else if (xhr.status !== 0) {
                  observer.error(xhr.response);
                }
              }
            };
            xhr.send();
          });
        });

      // Cleanup: unsubscribe + abort if incomplete
      return () => {
        if (sub) {
          sub.unsubscribe();
        }
        if (abortListener) {
          abortListener.unsubscribe();
        }
        if (xhr && xhr.readyState < 4) {
          isAborted = true; // Mark as aborted on cleanup too
          xhr.abort();
        }
      };
    });

    return request$;
  };

  // -------- POST with custom filter logic, then delegate ————
  // TODO: Refactor this to use an options object rather than these weird arguments
  public post = (
    endpoint: string,
    content?: Body,
    overrideSearchQuery?: SearchQuery,
    searchCriteria: string = null,
    except: string[] = null
  ): Observable<Record<any, any>> => {
    endpoint = !endpoint.indexOf('/') ? endpoint : '/' + endpoint;
    if (!content) {
      content = new ProductQuery();
    }
    const postBody: any = {};
    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        if (token && token.AccessToken) {
          const filterParams = this.filterService.getFilterParams();
          // Custom filter logic
          if (filterParams !== undefined) {
            if (
              _.isNil((content as ProductQuery).RouseCategoryList) ||
              !(content as ProductQuery).RouseCategoryList.length
            ) {
              if (
                filterParams.RouseCategoryList &&
                !!filterParams.RouseCategoryList.length
              ) {
                (content as ProductQuery).RouseCategoryList =
                  filterParams.RouseCategoryList.filter((f) => !!f).slice();
              }
            }
            if (except) {
              except.forEach((key) => {
                delete filterParams[key];
              });
            }
          }
          if ((content as ProductQuery)?.UseRentedAsProductType) {
            (content as ProductQuery).ExcludeOperated = null;
          }
          Object.assign(postBody, overrideSearchQuery || filterParams);
          Object.assign(postBody, content);
          if (searchCriteria) {
            postBody.SearchCriteria = searchCriteria;
          }
          if (
            this.authenticationService &&
            this.authenticationService._userInfoView &&
            this.authenticationService._userInfoView
          ) {
            postBody.ClientId =
              this.authenticationService._userInfoView.SelectedClient.ClientID.toString();
          }
          // Delegate actual HTTP request
          return this.simpleRdoHttpService.post(endpoint, postBody);
        }
        return EMPTY;
      })
    );
  };

  public pivotPost = (
    endpoint: string,
    content?: Body
  ): Observable<Record<any, any>> =>
    this.simpleRdoHttpService.post(endpoint, content);

  public getf(...args: any[]): Observable<Record<any, any>> {
    return this.getInternal({ args });
  }

  public getfx(except: string[], ...args: any[]): Observable<Record<any, any>> {
    return this.getInternal({ except, args });
  }

  private getInternal(req: any): Observable<Record<any, any>> {
    const except = req.except;
    const args = req.args;

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        let endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        args[1] = args[1] || {};
        let searchParams: QueryParams = null;
        const currentParams = new QueryParams();
        if (args.length > 1 && args[1]) {
          searchParams = args[1].search as QueryParams;
        }
        if (
          this.authenticationService &&
          this.authenticationService._userInfoView &&
          this.authenticationService._userInfoView.SelectedClient
        ) {
          let delimit = '?';
          if (endpoint.toLowerCase().indexOf('clientid=') === -1) {
            if (endpoint.indexOf('?') > -1) {
              delimit = '&';
            }
            endpoint =
              endpoint +
              delimit +
              'ClientId=' +
              this.authenticationService._userInfoView.SelectedClient.ClientID.toString();
          }
        }

        return this.filterService.filterParamsForGet.pipe(
          switchMap((f) => {
            searchParams = searchParams || new QueryParams();
            currentParams.appendAll(f);

            // RDO-specific filter logic
            if (searchParams.has('RouseCategoryList')) {
              currentParams.delete('RouseCategoryList');
            }
            if (except) {
              _.forEach(except, (p) => {
                currentParams.delete(p);
              });
            }
            searchParams.appendAll(currentParams);
            // End RDO-specific filter logic

            if (token && token.AccessToken) {
              // Delegate actual HTTP request
              const finalArgs = [endpoint, { search: searchParams }];
              return this.simpleRdoHttpService.get(...finalArgs);
            }
            return EMPTY;
          })
        );
      })
    );
  }

  private normalizeBodyParameter = (key: string, value: string[]): any => {
    const collectionParams = [
      'CycleBillRange',
      'RegionList',
      'DistrictList',
      'BranchList',
    ];
    if (collectionParams.indexOf(key) > -1) {
      return value;
    }
    return value.length === 1 ? value[0] : value;
  };

  // File upload delegates to simpleRdoHttpService.post
  public uploadFileToBucket = (
    clientid: number,
    f: File
  ): Observable<Record<any, any>> => {
    const endpoint = '/upload';
    const formdata = new FormData();

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        if (token && token.AccessToken) {
          formdata.append(f.name, f);
          if (
            this.authenticationService &&
            this.authenticationService._userInfoView &&
            this.authenticationService._userInfoView
          ) {
            formdata.append(
              'ClientId',
              this.authenticationService._userInfoView.SelectedClient.ClientID.toString()
            );
          }
          // Delegate actual HTTP request
          return this.simpleRdoHttpService.post(endpoint, formdata);
        }
        return EMPTY;
      })
    );
  };
}
